<div class="admin-panel-container">
  <!-- Header -->
  <div class="admin-header">
    <div class="header-content">
      <h1 class="admin-title">
        <i class="material-icons">admin_panel_settings</i>
        Admin Panel
      </h1>
      <div class="header-actions">
        <button
          class="btn btn-secondary"
          *ngIf="isAdminAuthenticated"
          [disabled]="isLoading"
          (click)="refreshDashboard()"
        >
          <i class="material-icons">refresh</i>
          Refresh
        </button>
        <button class="btn btn-outline" (click)="goToHome()">
          <i class="material-icons">home</i>
          Home
        </button>
        <button class="btn btn-danger" *ngIf="isAdminAuthenticated" [disabled]="isLoading" (click)="logoutAdmin()">
          <i class="material-icons">logout</i>
          Logout
        </button>
      </div>
    </div>

    <!-- Session Info -->
    <div class="session-info" *ngIf="isAdminAuthenticated && sessionInfo.expiresAt">
      <i class="material-icons">schedule</i>
      Session expires in: {{ sessionInfo.timeRemaining }}
    </div>
  </div>

  <!-- Main Content -->
  <div class="admin-content">
    <!-- Code Verification Form -->
    <div class="verification-container" *ngIf="showCodeInput">
      <div class="verification-card">
        <div class="verification-header">
          <i class="material-icons">security</i>
          <h2>Admin Access Verification</h2>
          <p>Enter the 6-digit admin code to access the admin panel</p>
        </div>

        <div class="verification-form">
          <div class="code-input-container">
            <input
              class="code-input"
              maxlength="6"
              pattern="[0-9]*"
              placeholder="000000"
              type="text"
              [class.error]="codeError"
              [disabled]="isLoading"
              [(ngModel)]="adminCode"
              (input)="onCodeInput()"
              (keypress)="onCodeKeyPress($event)"
            />
          </div>

          <div class="error-message" *ngIf="codeError">
            <i class="material-icons">error</i>
            {{ codeError }}
          </div>

          <button
            class="btn btn-primary verify-btn"
            [disabled]="isLoading || adminCode.length !== 6"
            (click)="verifyAdminCode()"
          >
            <i class="material-icons spinning" *ngIf="isLoading">hourglass_empty</i>
            <i class="material-icons" *ngIf="!isLoading">verified_user</i>
            {{ isLoading ? 'Verifying...' : 'Verify Access' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Dashboard -->
    <div class="dashboard-container" *ngIf="isAdminAuthenticated && !showCodeInput">
      <!-- Loading State -->
      <div class="loading-container" *ngIf="isLoading && !dashboardData">
        <i class="material-icons spinning">hourglass_empty</i>
        <p>Loading dashboard data...</p>
      </div>

      <!-- Error State -->
      <div class="error-container" *ngIf="dashboardError">
        <i class="material-icons">error</i>
        <h3>Error Loading Dashboard</h3>
        <p>{{ dashboardError }}</p>
        <button class="btn btn-primary" (click)="loadDashboardData()">
          <i class="material-icons">refresh</i>
          Retry
        </button>
      </div>

      <!-- Dashboard Content -->
      <div class="dashboard-grid" *ngIf="dashboardData && !isLoading">
        <!-- Users Statistics -->
        <div class="dashboard-card">
          <div class="card-header">
            <h3>
              <i class="material-icons">people</i>
              Users
            </h3>
          </div>
          <div class="card-content">
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.users.total }}</span>
              <span class="stat-label">Total Users</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.users.active }}</span>
              <span class="stat-label">Active Users</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.users.superusers }}</span>
              <span class="stat-label">Superusers</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.users.verified }}</span>
              <span class="stat-label">Verified Users</span>
            </div>
          </div>
        </div>

        <!-- Subscription Statistics -->
        <div class="dashboard-card">
          <div class="card-header">
            <h3>
              <i class="material-icons">card_membership</i>
              Subscriptions
            </h3>
          </div>
          <div class="card-content">
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.subscriptions.plus_users }}</span>
              <span class="stat-label">Plus Users</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.subscriptions.pro_users }}</span>
              <span class="stat-label">Pro Users</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.subscriptions.free_tier_users }}</span>
              <span class="stat-label">Free Tier Users</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.subscriptions.total_subscribers }}</span>
              <span class="stat-label">Total Subscribers</span>
            </div>
          </div>
        </div>

        <!-- Content Statistics -->
        <div class="dashboard-card">
          <div class="card-header">
            <h3>
              <i class="material-icons">library_books</i>
              Content
            </h3>
          </div>
          <div class="card-content">
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.content.words }}</span>
              <span class="stat-label">Words</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.content.pairs }}</span>
              <span class="stat-label">Pairs</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.content.dictionaries }}</span>
              <span class="stat-label">Dictionaries</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.content.categories }}</span>
              <span class="stat-label">Categories</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.content.tags }}</span>
              <span class="stat-label">Tags</span>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="dashboard-card">
          <div class="card-header">
            <h3>
              <i class="material-icons">trending_up</i>
              Today's Activity
            </h3>
          </div>
          <div class="card-content">
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.recent_activity.new_users_today }}</span>
              <span class="stat-label">New Users</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.recent_activity.new_words_today }}</span>
              <span class="stat-label">New Words</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ dashboardData.recent_activity.new_dictionaries_today }}</span>
              <span class="stat-label">New Dictionaries</span>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="dashboard-card">
          <div class="card-header">
            <h3>
              <i class="material-icons">settings</i>
              Quick Actions
            </h3>
          </div>
          <div class="card-content quick-actions">
            <button class="action-btn" (click)="navigateToUserManagement()">
              <i class="material-icons">people</i>
              Manage Users
            </button>
            <button class="action-btn" (click)="navigateToAI()">
              <i class="material-icons">auto_awesome</i>
              AI
            </button>
            <button class="action-btn" disabled>
              <i class="material-icons">query_stats</i>
              View Analytics
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
